import { Box, Flex, Link, Text } from "@chakra-ui/react";
import { RiAddLine } from "@remixicon/react";
import { Link as RouterLink } from "react-router";

import { Restricted } from "@/app/components/Restricted";
import EmptyState from "@/common/components/EmptyState";
import { IcoFeature, IcoFeatureView, IcoUser } from "@/common/components/Icons";
import { RouterLinkButton } from "@/common/components/RouterLinkButton";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useFlag } from "@reflag/react-sdk";
import { FlagViewDTO } from "@reflag/shared/flagViewAPI";
import { GlobalSettingsUrl } from "@reflag/shared/urls";

function NewFlagButton() {
  return (
    <Restricted role="contributor">
      <RouterLinkButton
        leftIcon={<RiAddLine size={16} />}
        size="sm"
        to="new"
        variant="outline"
      >
        New flag
      </RouterLinkButton>
    </Restricted>
  );
}

export function FlagsEmptyState({
  view,
  isLoading,
  isError,
  onRetry,
}: {
  view: FlagViewDTO;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
}) {
  const { env } = useEnvironment();

  const { isEnabled: importEnabled } = useFlag("launchdarkly-import");

  if (view.type === "all") {
    return (
      <EmptyState
        action={
          <Flex align="center" direction={"row"} gap={2}>
            <NewFlagButton />
            {importEnabled && (
              <Box>
                or{" "}
                <Link as={RouterLink} to={GlobalSettingsUrl(env, "app-import")}>
                  import flags
                </Link>
              </Box>
            )}
          </Flex>
        }
        description={
          <Text>
            Protip: You can also create flags with our CLI, MCP or Linear
            integration.
          </Text>
        }
        error="Failed to load flags"
        flexGrow={1}
        icon={<IcoFeature boxSize="48px" />}
        isError={isError}
        isLoading={isLoading}
        maxW="md"
        title="Create your first flag"
        onRetry={onRetry}
      />
    );
  }

  if (view.type === "my") {
    return (
      <EmptyState
        description={
          <Text>
            You don&apos;t have any flags assigned to you yet. Create new flags
            or assign existing flags to you.
          </Text>
        }
        error="Failed to load your flags"
        flexGrow={1}
        icon={<IcoUser boxSize="48px" />}
        isError={isError}
        isLoading={isLoading}
        maxW="md"
        title="No flags assigned to you"
        onRetry={onRetry}
      />
    );
  }

  if (view.type === "archived") {
    return (
      <EmptyState
        error="Failed to load archived flags"
        flexGrow={1}
        icon={<IcoFeature boxSize="48px" />}
        isError={isError}
        isLoading={isLoading}
        maxW="md"
        title="No archived flags found"
        onRetry={onRetry}
      />
    );
  }

  if (view.type === "stale") {
    return (
      <EmptyState
        description={
          <Text>
            Flags turn stale after they&apos;ve been rolled out to everyone.
            Adjust the grace period in{" "}
            <Link
              as={RouterLink}
              to={GlobalSettingsUrl(env, "org-archiving-flow")}
            >
              Archive settings
            </Link>
          </Text>
        }
        error="Failed to load stale flags"
        flexGrow={1}
        icon={<IcoFeature boxSize="48px" />}
        isError={isError}
        isLoading={isLoading}
        maxW="md"
        title="No stale flags found"
        onRetry={onRetry}
      />
    );
  }

  return (
    <EmptyState
      description="Use this view to group flags and customize the table columns to show exactly what you need."
      error="Failed to load flags"
      flexGrow={1}
      icon={<IcoFeatureView boxSize="48px" />}
      isError={isError}
      isLoading={isLoading}
      title="No flags in view"
      onRetry={onRetry}
    />
  );
}
